// IMG-10 Calibrate (K): click two points a known distance apart, type the real distance.
// Both points on ONE image → scale that image; otherwise offer to scale the whole drawing.
import { dist, type Vec } from "../core/geometry";
import { formatLength, parseScalar } from "../core/units";
import type { Doc, ImageEntity } from "../doc/entities";
import { pointInImage } from "../engine/imageGeom";
import type { RenderView } from "../engine/renderEntities";
import { drawReadout } from "./line";
import { scaleAllPatches } from "./entityOps";
import type { Tool, ToolCtx, ToolKeyEvent, ToolPointerEvent } from "./types";

export class CalibrateTool implements Tool {
  readonly id = "calibrate" as const;
  private pts: Vec[] = [];
  private cursor: Vec | null = null;

  constructor(private ctx: ToolCtx) {}

  activate(): void {
    this.reset();
  }

  deactivate(): void {
    this.reset();
  }

  cancel(): void {
    this.reset(); // EC-IMG-12
  }

  inProgress(): boolean {
    return this.pts.length > 0;
  }

  private reset(): void {
    this.pts = [];
    this.cursor = null;
    this.ctx.setSnapBase(null);
    this.ctx.setMeasure("none", "");
    this.ctx.setHint("Calibrate: click two points a known distance apart (e.g. a door width)");
    this.ctx.setCursorTip(null);
    this.ctx.requestRender();
  }

  pointerDown(e: ToolPointerEvent): void {
    if (e.button !== 0) return;
    if (this.pts.length >= 2) return; // waiting for the typed distance
    if (this.pts.length === 1 && dist(e.point, this.pts[0]!) < 1e-9) return; // EC-IMG-11
    this.pts.push(e.point);
    if (this.pts.length === 1) {
      this.ctx.setSnapBase(e.point);
      this.ctx.setHint("Calibrate: click the second point");
      this.ctx.setCursorTip("second point");
    } else {
      this.ctx.setMeasure("length", "real distance");
      this.ctx.setHint("Calibrate: type the REAL distance between the points ⏎ (e.g. 820 or 3.6m)");
      this.ctx.setCursorTip("type the real distance ⏎");
    }
    this.ctx.requestRender();
  }

  pointerMove(e: ToolPointerEvent): void {
    this.cursor = e.point;
    this.ctx.requestRender();
  }

  pointerUp(): void {}

  doubleClick(): void {}

  key(e: ToolKeyEvent): boolean {
    if (e.key === "Escape" && this.pts.length > 0) {
      this.reset();
      return true;
    }
    if ((e.key === "Backspace" || e.key === "Delete") && this.pts.length > 0) {
      this.pts.pop();
      this.ctx.setMeasure("none", "");
      this.ctx.setSnapBase(this.pts[0] ?? null); // keep the ray anchor on the surviving point
      this.ctx.setHint(
        this.pts.length === 1 ? "Calibrate: click the second point" : "Calibrate: click the first point"
      );
      this.ctx.setCursorTip(this.pts.length === 1 ? "second point" : null);
      this.ctx.requestRender();
      return true;
    }
    return false;
  }

  measurement(raw: string): boolean {
    if (this.pts.length !== 2) return false;
    const { units } = this.ctx.docUnits();
    const real = parseScalar(raw, units);
    if (real === null || real <= 1e-9) return false; // EC-IMG-11
    const measured = dist(this.pts[0]!, this.pts[1]!);
    if (measured < 1e-9) return false;
    const factor = real / measured;
    if (factor < 0.001 || factor > 1000) {
      // EC-IMG-13 likely-typo guard
      if (!confirm(`That scales by ×${factor.toPrecision(3)} — is that right?`)) return false;
    }
    const doc = this.ctx.store.doc;
    const image = this.imageUnderBoth(doc);
    if (image) {
      // IMG-10: scale that image about its pos — everything traced over it stays put
      this.ctx.store.dispatch({
        kind: "updateEntities",
        patches: [
          {
            id: image.id,
            before: { wMm: image.wMm, hMm: image.hMm },
            after: { wMm: image.wMm * factor, hMm: image.hMm * factor }
          }
        ]
      });
      this.ctx.setHint(
        `Image calibrated — 1 px ≈ ${((image.wMm * factor) / (doc.assets[image.assetHash]?.w ?? 1)).toFixed(3)} mm. Press V to select.`
      );
    } else {
      if (!confirm(`No single image under both points. Scale the ENTIRE drawing by ×${factor.toPrecision(4)}?`)) {
        this.reset();
        return true;
      }
      const patches = scaleAllPatches(doc, factor);
      if (patches.length > 0) this.ctx.store.dispatch({ kind: "updateEntities", patches });
      this.ctx.setHint("Drawing calibrated.");
    }
    this.pts = [];
    this.ctx.setMeasure("none", "");
    this.ctx.setSnapBase(null);
    this.ctx.setCursorTip(null);
    this.ctx.requestRender();
    return true;
  }

  private imageUnderBoth(doc: Doc): ImageEntity | null {
    // topmost image containing BOTH points
    for (let li = doc.layers.length - 1; li >= 0; li--) {
      const layer = doc.layers[li]!;
      const order = doc.zOrder[layer.id] ?? [];
      for (let zi = order.length - 1; zi >= 0; zi--) {
        const e = doc.entities[order[zi]!];
        if (e?.type === "image" && !e.hidden && pointInImage(e, this.pts[0]!) && pointInImage(e, this.pts[1]!)) {
          return e;
        }
      }
    }
    return null;
  }

  renderDynamic(v: RenderView): void {
    if (this.pts.length === 0) return;
    const { ctx } = v;
    const to = this.pts[1] ?? this.cursor;
    ctx.strokeStyle = "#e0b34d";
    ctx.lineWidth = 1.5 / v.cam.scale;
    ctx.setLineDash([4 / v.cam.scale, 3 / v.cam.scale]);
    ctx.beginPath();
    ctx.moveTo(this.pts[0]!.x, this.pts[0]!.y);
    if (to) ctx.lineTo(to.x, to.y);
    ctx.stroke();
    ctx.setLineDash([]);
    for (const p of this.pts) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, 4 / v.cam.scale, 0, Math.PI * 2);
      ctx.fillStyle = "#e0b34d";
      ctx.fill();
    }
    if (to) {
      const { units, precision } = this.ctx.docUnits();
      drawReadout(v, to, `measured ${formatLength(dist(this.pts[0]!, to), units, precision)}`);
    }
  }
}
